// src/pages/Users/Galleries/hooks/useGalleriesController.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { getAllGalleries } from "../../../../services/gallerieServices";

import {
  ALBUMS_STORAGE_KEY,
  DEFAULT_ALBUMS_UI,
  computeGalleryStats,
  filterAndSortGalleries,
  readAlbumsStorage,
} from "../utils/galleriesUtils";

export default function useGalleriesController(isManager = false) {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [ui, setUi] = useState(() => readAlbumsStorage());
  const [searchInput, setSearchInput] = useState(ui.q || "");

  const persistUi = useCallback((nextState) => {
    setUi(nextState);
    localStorage.setItem(ALBUMS_STORAGE_KEY, JSON.stringify(nextState));
  }, []);

  const updateUi = useCallback((patch) => {
    setUi((prev) => {
      const next = {
        ...prev,
        ...patch,
      };

      localStorage.setItem(ALBUMS_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setUi((prev) => {
        if (prev.q === searchInput) return prev;

        const next = {
          ...prev,
          q: searchInput,
        };

        localStorage.setItem(ALBUMS_STORAGE_KEY, JSON.stringify(next));
        return next;
      });
    }, 250);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const refreshGalleries = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await getAllGalleries();
      setGalleries(Array.isArray(response) ? response.filter(Boolean) : []);
    } catch (err) {
      console.error("Failed to fetch galleries:", err);
      setGalleries([]);
      setError("Impossible de charger les galeries pour le moment.");
      toast.error("Erreur de chargement des galeries.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshGalleries();
  }, [refreshGalleries]);

  const filtered = useMemo(
    () => filterAndSortGalleries(galleries, ui, isManager),
    [galleries, ui, isManager]
  );

  const stats = useMemo(
    () => computeGalleryStats(galleries),
    [galleries]
  );

  const resetFilters = useCallback(() => {
    const next = {
      ...DEFAULT_ALBUMS_UI,
      showAll: false,
    };

    persistUi(next);
    setSearchInput(next.q);
  }, [persistUi]);

  return {
    loading,
    error,
    galleries,
    filtered,
    stats,
    ui,
    searchInput,
    setSearchInput,
    updateUi,
    resetFilters,
    refreshGalleries,
  };
}